import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SvgFragment from '../../../../fragments/SvgFragment';
import { styles } from '../../../../styles/themes/style';
import { ICampaign } from '../../../../interfaces/models/campaign';
import { ISignalLead } from '../../../../interfaces/models/signals';
import { useCampaignsRequest, ICsvLeadRow } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useSignalsRequest } from '../../../../services/apis/signals/useSignalsRequest';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { useAppActions } from '../../../../stores/application/useAppActions';
import { filterEnrollableCampaigns } from '../../../../utils/campaignContactUtils';
import { plural } from './signalsUi';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', active: 'Active', paused: 'Paused' };

interface IAddToCampaignDialogProps {
  show: boolean;
  leads: ISignalLead[];
  onHide: () => void;
  /** After a successful add — reload the list. */
  onDone: () => void;
}

/** A signal lead as a campaign import row. Sales Navigator ids have no vanity, member ids do. */
function toRow(lead: ISignalLead): ICsvLeadRow {
  const p = lead.person;
  const name = p.name || [p.firstName, p.lastName].filter(Boolean).join(' ');
  return {
    ...(p.publicIdentifier ? { publicIdentifier: p.publicIdentifier } : {}),
    ...(p.providerId ? { linkedinId: p.providerId } : {}),
    name,
    firstName: p.firstName || name.split(' ')[0] || '',
    lastName: p.lastName || name.split(' ').slice(1).join(' '),
    title: p.headline || '',
    linkedinUrl: p.profileUrl || '',
    imageUrl: p.profilePicture || '',
  };
}

/**
 * Puts the chosen signal leads into one of your campaigns, through the same import the
 * CSV and post-commenter sources use — so do-not-contact lists, duplicates and the
 * campaign's own checks apply exactly as everywhere else. Only campaigns you created
 * can take leads (the API enforces it).
 */
const AddToCampaignDialog: FunctionComponent<IAddToCampaignDialogProps> = ({ show, leads, onHide, onDone }) => {
  const campaignsApi = useCampaignsRequest();
  const signalsApi = useSignalsRequest();
  const apiRef = useRef({ campaignsApi, signalsApi });
  apiRef.current = { campaignsApi, signalsApi };
  const { store: { user } } = useAuthStore();
  const { setToast } = useAppActions();

  const [campaigns, setCampaigns] = useState<ICampaign[] | null>(null);
  const [campaignId, setCampaignId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    let stale = false;
    setCampaigns(null);
    setCampaignId('');
    (async () => {
      const data = await apiRef.current.campaignsApi.getCampaigns('signals-campaign-targets');
      if (stale) return;
      const enrollable = filterEnrollableCampaigns(data?.campaigns ?? [], user?._id);
      setCampaigns(enrollable);
      if (enrollable.length === 1) setCampaignId(enrollable[0]._id);
    })();
    return () => { stale = true; };
  }, [show, user?._id]);

  const usable = leads.filter(l => l.person.publicIdentifier || l.person.providerId);

  const submit = async () => {
    if (!campaignId || !usable.length) return;
    setSaving(true);
    const result = await apiRef.current.campaignsApi.enrollFromCsv(campaignId, usable.map(toRow), { source: 'signals' });
    if (!result) {
      setSaving(false);
      setToast({ type: 'error', message: 'Could not add these people to the campaign.' });
      return;
    }

    const res = await apiRef.current.signalsApi.updateLeadStatus(usable.map(l => l._id), 'in_campaign', campaignId);
    setSaving(false);
    if (!res?.success) setToast({ type: 'error', message: res?.message || 'Added to the campaign, but could not update the Signals list.' });

    const enrolled = Number(result.enrolled) || 0;
    const already = Number(result.alreadyEnrolled) || 0;
    const suppressed = Number(result.suppressed) || 0;
    const parts = [`${plural(enrolled, 'person', 'people')} added`];
    if (already) parts.push(`${already} already in it`);
    if (suppressed) parts.push(`${suppressed} on a do-not-contact list`);
    setToast({ type: enrolled ? 'success' : 'info', message: `${parts.join(', ')}.` });

    onDone();
    onHide();
  };

  const name = campaigns?.find(c => c._id === campaignId)?.name;

  return (
    <StyledModal show={show} onHide={onHide} width="460px">
      <Body>
        <Header>
          <Title>Add to campaign</Title>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </Header>

        <Lead>
          {plural(usable.length, 'person', 'people')} from Signals
          {usable.length < leads.length && ` (${leads.length - usable.length} without a LinkedIn profile are skipped)`}.
        </Lead>

        {campaigns === null && <Hint>Loading your campaigns…</Hint>}
        {campaigns?.length === 0 && (
          <Hint>You have no draft, active or paused campaign of your own. Create one first, then come back.</Hint>
        )}
        {!!campaigns?.length && (
          <List role="radiogroup" aria-label="Campaign">
            {campaigns.map(c => (
              <Option key={c._id} $active={c._id === campaignId}>
                <input
                  type="radio"
                  name="signals-campaign"
                  value={c._id}
                  checked={c._id === campaignId}
                  onChange={() => setCampaignId(c._id)}
                />
                <OptionName>{c.name}</OptionName>
                <OptionStatus>{STATUS_LABELS[c.status] || c.status}</OptionStatus>
              </Option>
            ))}
          </List>
        )}

        <Footer>
          <SecondaryBtn type="button" onClick={onHide}>Cancel</SecondaryBtn>
          <PrimaryBtn type="button" disabled={!campaignId || !usable.length || saving} onClick={submit}>
            {saving ? 'Adding…' : name ? `Add to “${name}”` : 'Add to campaign'}
          </PrimaryBtn>
        </Footer>
      </Body>
    </StyledModal>
  );
};

export default AddToCampaignDialog;

// ─── Styles ───────────────────────────────────────────────────────────────────

const Body = styled.div`
  display: flex; flex-direction: column; gap: 14px;
  padding: 20px 24px 24px;
`;

const Header = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: NunitoSans; font-size: 18px; font-weight: 800;
  color: ${styles.colors.black600};
`;

const CloseBtn = styled.button`
  background: none; border: none; padding: 0; cursor: pointer; line-height: 0;
  .svg { width: 20px; height: 20px; }
`;

const Lead = styled.p`
  margin: 0;
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black500};
`;

const Hint = styled.p`
  margin: 0; padding: 12px; border-radius: 10px; background: ${styles.colors.black100};
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black400};
`;

const List = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  max-height: 280px; overflow-y: auto;
`;

const Option = styled.label<{ $active: boolean }>`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 10px; cursor: pointer;
  border: 1.5px solid ${({ $active }) => ($active ? styles.colors.primary600 : '#dde3ee')};
  background: ${({ $active }) => ($active ? styles.colors.primary100 : styles.colors.white)};
  input { accent-color: ${styles.colors.primary600}; }
`;

const OptionName = styled.span`
  flex: 1; min-width: 0;
  font-family: NunitoSans; font-size: 13px; font-weight: 700; color: ${styles.colors.black600};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const OptionStatus = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400};
`;

const Footer = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  margin-top: 6px;
`;

const SecondaryBtn = styled.button`
  height: 38px; padding: 0 16px; border-radius: 8px;
  border: 1px solid #dde3ee; background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  color: ${styles.colors.black500}; cursor: pointer;
  &:hover { background: #f7f9fc; }
`;

const PrimaryBtn = styled.button`
  height: 38px; padding: 0 18px; border-radius: 8px; border: none;
  background: ${styles.colors.primary600}; color: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; font-weight: 700; cursor: pointer;
  max-width: 280px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  &:hover:not(:disabled) { background: ${styles.colors.primary500}; }
  &:disabled { opacity: 0.5; cursor: default; }
`;
